import { Skeleton, Stack, Typography } from '@mui/material';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SelectMenu } from 'src/components/SelectMenu';
import { panelFieldLabelSx } from 'src/pages/Settings/memberships/styles';

type ClientSegment = {
  brandId: string | number;
  name?: string | null;
};

const SELECT_SKELETON_HEIGHT_REM = '2rem';

export interface ClientSegmentSelectorProps {
  segments: ClientSegment[];
  loading: boolean;
  selectedSegmentId?: string | number | null;
  onSegmentChange?: (segmentId: string | null) => boolean | Promise<boolean | void>;
  disabled?: boolean;
  className?: string;
}

export const ClientSegmentSelector = ({
  segments,
  loading,
  selectedSegmentId,
  onSegmentChange,
  disabled = false,
  className,
}: ClientSegmentSelectorProps) => {
  const [internalValue, setInternalValue] = useState('');
  // Guards against stale async completions (e.g., confirmation modal resolves after unmount).
  // Incrementing this invalidates any in-flight `tryApplySegment` call that started earlier.
  const segmentChangeTokenRef = useRef(0);

  const currentValue = internalValue ?? '';
  const hasSegments = segments.length > 0;

  useEffect(() => {
    if (typeof selectedSegmentId === 'number' || typeof selectedSegmentId === 'string') {
      setInternalValue(String(selectedSegmentId));
      return;
    }

    setInternalValue('');
  }, [selectedSegmentId]);

  useEffect(() => {
    return () => {
      segmentChangeTokenRef.current += 1;
    };
  }, []);

  const canProceedWith = useCallback(
    async (segmentId: string | null) => {
      return (await onSegmentChange?.(segmentId)) ?? true;
    },
    [onSegmentChange],
  );

  const tryApplySegment = useCallback(
    async (segmentId: string | null, internalValueToSet?: string) => {
      const token = segmentChangeTokenRef.current + 1;
      segmentChangeTokenRef.current = token;
      const canProceed = await canProceedWith(segmentId);
      if (!canProceed) {
        return false;
      }

      if (token !== segmentChangeTokenRef.current) {
        return false;
      }

      setInternalValue(internalValueToSet ?? (segmentId ? String(segmentId) : ''));
      return true;
    },
    [canProceedWith],
  );

  const handleSegmentChange = useCallback(
    async (nextValue: string) => {
      await tryApplySegment(nextValue || null, nextValue);
    },
    [tryApplySegment],
  );

  const segmentOptions = useMemo(
    () =>
      segments.map((segment) => ({
        value: String(segment.brandId),
        title: segment.name || 'Untitled segment',
      })),
    [segments],
  );

  let body: ReactNode;

  if (loading) {
    body = <Skeleton variant="rounded" height={SELECT_SKELETON_HEIGHT_REM} />;
  } else if (!hasSegments) {
    body = (
      <Typography variant="body2" color="text.secondary">
        No client segments are configured for this partner yet.
      </Typography>
    );
  } else {
    body = (
      <SelectMenu
        label="Client segment"
        options={segmentOptions}
        value={currentValue || null}
        onChange={handleSegmentChange}
        placeholder="Select a client segment"
        disabled={disabled}
        menuWidth="trigger"
        sx={{ width: '100%' }}
      />
    );
  }

  return (
    <Stack spacing={1} className={className}>
      <Typography variant="caption" sx={panelFieldLabelSx}>
        Client segment
      </Typography>
      {body}
    </Stack>
  );
};
